const CASE_ENABLED_MODES = ["casesOnly", "casesAndSingles"];
const VALID_OVERRIDE_MODES = [
  "inherit",
  "casesOnly",
  "casesAndSingles",
  "singlesOnly",
];

/**
 * LeafLink import copies `filters.state` onto `loadOrders.state`. The filter
 * is a string or a one-element array; anything else (multi-select, objects)
 * is not a stateID and must not be passed into variableCases matching.
 */
export function normalizeImportStateID(value) {
  if (typeof value === "string" && value) return value;
  if (
    Array.isArray(value) &&
    value.length === 1 &&
    typeof value[0] === "string" &&
    value[0]
  ) {
    return value[0];
  }
  return null;
}

/**
 * Resolves the effective sold-by-case/singles rule for a product, giving a
 * product-level override priority over its category default.
 *
 * `licenseeTier` is accepted but unused today — reserved so the deferred
 * licensee/state tier can slot in ahead of the product-override check
 * without changing this function's signature.
 */
export function resolveCaseRestriction(product, category, context = {}, licenseeTier = null) {
  const override = product?.restrictionOverride;
  if (
    override?.mode &&
    override.mode !== "inherit" &&
    VALID_OVERRIDE_MODES.includes(override.mode)
  ) {
    return {
      soldByCase: override.mode,
      caseQTY: CASE_ENABLED_MODES.includes(override.mode) ? Number(override.caseQTY) || 0 : 0,
      source: "product",
    };
  }
  return resolveCategoryOnly(category, context);
}

function resolveCategoryOnly(category, context = {}) {
  const flags = category?.flags || [];
  const soldByCase = flags.includes("soldByCaseOnly")
    ? "casesOnly"
    : flags.includes("soldByCase")
    ? "casesAndSingles"
    : "singlesOnly";

  let caseQTY = CASE_ENABLED_MODES.includes(soldByCase) ? category?.caseQTY || 0 : 0;
  if (
    CASE_ENABLED_MODES.includes(soldByCase) &&
    flags.includes("variableCases") &&
    Array.isArray(category?.variableCases)
  ) {
    const match = category.variableCases.find((v) => v.stateID === context.stateID);
    if (match) caseQTY = match.qty;
  }
  return { soldByCase, caseQTY, source: "category" };
}

// Both helpers read `.soldByCase` (this module's resolved-restriction shape),
// not `.mode` — a locked line item's `caseRestriction` object uses `.mode` and
// must be passed as `{ soldByCase: caseRestriction.mode, caseQTY }`, or pass
// the cart item itself if its own top-level `soldByCase`/`caseQTY` are kept in
// sync with the lock (as they are in SalesOrder_New/View.vue).
export function allowsCaseQuantities(resolved) {
  return resolved.soldByCase !== "singlesOnly" && resolved.caseQTY > 0;
}

export function allowsSingleQuantities(resolved) {
  return resolved.soldByCase !== "casesOnly";
}

/**
 * Compares a line item's previously locked case/singles restriction against a
 * freshly resolved one, to decide whether re-establishing the lock after an
 * unlock (qty dropped to zero) should surface a "rule changed" notice.
 *
 * Returns false when there was no prior lock to compare against — a genuine
 * first-ever add never warrants a change notice.
 */
export function hasRestrictionChanged(previous, next) {
  if (!previous) return false;
  return previous.mode !== next.mode || previous.caseQTY !== next.caseQTY;
}
